/**
 * Traveller-merchant interaction flow (HEX-72 — wires the previously-inert
 * `traveller-merchant` tile; ADR-0012 §4 / ADR-0015 §6).
 *
 * A rare frontier vendor: a small selection skewed toward higher rarity
 * (rare/epic surface earlier than at town shops; `rollMerchantStock`), dice
 * included. **One-time**: after a single purchase the merchant is consumed —
 * the tile's lifecycle records `interacted: true`, the tile resolves, and the
 * worldview repaints a spent marker (HEX-71 resolved-tile pattern, reused via
 * `tileGlyphFor`). Leaving without buying does NOT consume it.
 *
 * Reuses the shop's card chrome (the chest/shop overlay precedent) — buy a card,
 * open the inventory editor to make room, close. Stock is rolled deterministically
 * from the tile so a returning visitor sees the same shelf until it is consumed.
 */

import { createRng } from '../combat/dice';
import { cardPrice, rollMerchantStock, shopStockSeed } from '../combat/shopStock';
import type { Card } from '../combat/types';
import { InventoryEditor } from '../combat/ui/InventoryEditor';
import { type AcquisitionSession, openAcquisition } from '../combat/ui/curationDrawer';
import { canAddCard } from '../combat/ui/inventoryEdit';
import type { Character } from '../core/character';
import type { Tile } from '../core/tile';
import type { Persistence } from '../persistence/Persistence';
import { createGlyphElement } from '../render/glyphs';
import { buildStallShell, buildWareTile, labelSpan } from './stallChrome';

import '../combat/ui/inventory-editor-styles.css';
import './interaction-modal-styles.css';

export interface MerchantFlowDeps {
  readonly persistence: Pick<Persistence, 'saveCharacter' | 'saveTileMutations'>;
}

export interface MerchantFlowStartOptions {
  readonly character: Character;
  readonly tile: Tile;
  readonly deps: MerchantFlowDeps;
  /** `consumed` is true when the merchant was spent (a card was bought) — the
   * host repaints the tile to show the spent marker. */
  readonly onResolved: (consumed: boolean) => void | Promise<void>;
  /** Test seam: inject a deterministic shelf instead of rolling one. */
  readonly cardStock?: readonly Card[];
}

export class MerchantFlow {
  private modal: HTMLDivElement | null = null;
  private body: HTMLDivElement | null = null;
  private goldLine: HTMLSpanElement | null = null;
  private stock: Card[] = [];
  private editor: InventoryEditor | null = null;
  private acquiring: AcquisitionSession | null = null;
  private active = false;

  constructor(private readonly container: HTMLElement) {}

  /**
   * Mount the merchant modal if the tile is an un-consumed traveller-merchant.
   * Returns true when mounted; false otherwise (wrong type / already spent).
   */
  start(opts: MerchantFlowStartOptions): boolean {
    if (this.active) return false;
    if (opts.tile.type !== 'traveller-merchant') return false;
    if (opts.tile.state === 'resolved') return false;
    const state = opts.tile.lifecycleState;
    if (state !== null && state.kind === 'travellerMerchant' && state.interacted) return false;

    this.active = true;
    this.stock = opts.cardStock ? [...opts.cardStock] : this.rollStock(opts);
    this.modal = this.mountModal(opts);
    this.renderBody(opts);
    return true;
  }

  isActive(): boolean {
    return this.active;
  }

  destroy(): void {
    this.editor?.destroy();
    this.editor = null;
    this.acquiring?.destroy();
    this.acquiring = null;
    this.modal?.remove();
    this.modal = null;
    this.body = null;
    this.goldLine = null;
    this.active = false;
  }

  private rollStock(opts: MerchantFlowStartOptions): Card[] {
    const seed = shopStockSeed(opts.tile.q, opts.tile.r);
    return rollMerchantStock(createRng(seed));
  }

  private mountModal(opts: MerchantFlowStartOptions): HTMLDivElement {
    const shell = buildStallShell({
      modalRole: 'merchant-modal',
      ariaLabel: 'Traveller-merchant',
      signTitle: 'The Wandering Merchant',
      signSubtitle: 'TRAVELLER · MOVES ON AFTER ONE SALE',
      goldRole: 'merchant-gold',
      greeting: '“Rare wares, traveller — but I travel light. Choose one; then I move on.”',
      waresRole: 'merchant-body',
    });
    this.body = shell.wares;
    this.goldLine = shell.goldValue;

    // The "moves on" reminder sits on the shelf, left of the actions.
    const note = document.createElement('div');
    note.className = 'hex6-stall__note';
    note.dataset.role = 'merchant-note';
    note.textContent = 'The merchant moves on after a single sale.';
    shell.shelf.prepend(note);

    const inventoryBtn = document.createElement('button');
    inventoryBtn.type = 'button';
    inventoryBtn.dataset.action = 'merchant-inventory';
    inventoryBtn.className = 'btn hex6-stall__action';
    inventoryBtn.append(
      createGlyphElement('chest', { size: 14, colour: 'var(--bone-mid)' }),
      labelSpan('Inventory'),
    );
    inventoryBtn.addEventListener('click', () => this.openInventory(opts));

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.dataset.action = 'merchant-close';
    closeBtn.className = 'btn ghost hex6-stall__action';
    closeBtn.textContent = 'Leave';
    closeBtn.addEventListener('click', () => {
      void this.close(opts, false);
    });

    shell.actions.append(inventoryBtn, closeBtn);

    this.container.appendChild(shell.root);
    return shell.root;
  }

  private renderBody(opts: MerchantFlowStartOptions): void {
    if (!this.body) return;
    const character = opts.character;

    const tiles = this.stock.map((card) =>
      buildWareTile({
        card,
        price: cardPrice(card),
        offerRole: 'merchant-card-offer',
        priceRole: 'merchant-card-price',
        buyAction: 'merchant-buy-card',
        affordable: character.gold >= cardPrice(card),
        fits: canAddCard(character.inventory, card),
        onBuy: () => this.buyCard(opts, card),
      }),
    );

    this.body.replaceChildren(...tiles);
    if (this.goldLine) this.goldLine.textContent = String(character.gold);
  }

  private buyCard(opts: MerchantFlowStartOptions, card: Card): void {
    const character = opts.character;
    const price = cardPrice(card);
    if (character.gold < price || !this.modal) return;
    // Present the bought card beside the loadout (HEX-149): place it (curating to
    // make room), or back out without buying. Charged only on a place.
    this.modal.style.display = 'none';
    const session = openAcquisition(this.container, character, {
      item: card,
      declineLabel: 'Leave it',
    });
    this.acquiring = session;
    void session.done.then((outcome) => {
      this.acquiring = null;
      if (outcome === 'declined') {
        // Backed out — merchant NOT consumed, gold untouched; persist any curation
        // destructions, then re-show the stall.
        if (this.modal) this.modal.style.display = '';
        void opts.deps.persistence.saveCharacter(character);
        this.renderBody(opts);
        return;
      }
      // Placed: charge + one sale consumes the merchant (ADR-0012 §4) — mark
      // interacted + resolved, then close signalling `consumed`.
      character.gold -= price;
      void this.close(opts, true);
    });
  }

  private openInventory(opts: MerchantFlowStartOptions): void {
    if (this.editor || !this.modal) return;
    this.modal.style.display = 'none';
    const editor = new InventoryEditor(opts.character.inventory);
    this.editor = editor;
    editor.mount(this.container, {
      inventory: opts.character.inventory,
      onChange: (inv) => {
        opts.character.inventory = inv;
      },
      onClose: () => {
        editor.destroy();
        this.editor = null;
        if (this.modal) this.modal.style.display = '';
        this.renderBody(opts);
      },
    });
  }

  private async close(opts: MerchantFlowStartOptions, consumed: boolean): Promise<void> {
    if (!this.active) return;
    const { character, tile, deps } = opts;
    if (consumed) {
      tile.state = 'resolved';
      if (tile.lifecycleState !== null && tile.lifecycleState.kind === 'travellerMerchant') {
        tile.lifecycleState = { ...tile.lifecycleState, interacted: true };
      }
    }
    this.destroy();
    await deps.persistence.saveCharacter(character);
    if (consumed) await deps.persistence.saveTileMutations(tile);
    await opts.onResolved(consumed);
  }
}
